import {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {useMap, useMapEvents} from 'react-leaflet';
import Supercluster from 'supercluster';

import {MAP_DEFAULTS} from '../../utils/constants';

import type {PointFeature, ClusterFeature} from 'supercluster';

export type AnyPropsWithId = {
  id: string;
};
function useRefOnce<T>(getDefaultValue: () => T): () => T {
  const ref = useRef<T | null>(null);
  const _getDefaultValue = useRef(getDefaultValue);

  const getter = useCallback(() => {
    if (ref.current !== null) {
      return ref.current;
    }

    ref.current = _getDefaultValue.current();

    return ref.current;
  }, []);

  return getter;
}

export function useCluster<P extends AnyPropsWithId>(
  markers: PointFeature<P>[],
  {maxZoom, minZoom}: {maxZoom: number; minZoom: number}
): [PointFeature<P>[], ClusterFeature<P>[], Supercluster<P, P>] {
  const [clusters, setClusters] = useState<PointFeature<P>[]>([]);
  const [strays, setStrays] = useState<ClusterFeature<P>[]>([]);

  // force simple re-render after map event
  const [update, setUpdate] = useState(0);

  const getSupercluster = useRefOnce(
    () =>
      new Supercluster<P, P>({
        radius: MAP_DEFAULTS.scRadius,
        maxZoom,
        minZoom,
      })
  );

  const map = useMap();

  if (!map) {
    throw new Error('Leaflet map is not defined.');
  }

  const handlers = useMemo(
    () => ({
      zoomend: () => setUpdate((count) => count + 1),
      moveend: () => setUpdate((count) => count + 1),
    }),
    []
  );

  useMapEvents(handlers);

  // grab values directly instead of storing duplicates in state
  const zoom = map.getZoom();
  const bounds = map.getBounds().toBBoxString();
  useEffect(() => {
    // prettier-ignore
    const boundsInts = bounds
      .split(',')
      .map((bound) => parseFloat(bound)) as [number, number, number, number]

    getSupercluster().load(markers);

    const features = getSupercluster().getClusters(boundsInts, zoom);

    const newClusters: PointFeature<P>[] = [];
    const newMarkers: ClusterFeature<P>[] = [];

    features.forEach((feature) => {
      if (feature.type === 'Feature' && feature.id) {
        const _id = feature.id;

        if (typeof _id !== 'number') throw new Error('Cluster id not integer');

        newClusters.push(feature);
      } else {
        // @ts-expect-error figure out how to properly lock in the type
        newMarkers.push(feature);
      }
    });

    setClusters(newClusters);
    setStrays(newMarkers);
  }, [update, markers, bounds, zoom, getSupercluster]);

  // @ts-expect-error figure out how to properly lock in the type
  return [strays, clusters, getSupercluster()];
}
